import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { ReactComponent as Lightbulb } from "../../../assets/img/icons/Lightbulb.svg";
import { ReactComponent as PreviewArticleContentLoader } from "../../../assets/img/Loaders/PreviewArticleContentLoader.svg";
import { RootState } from "store";
import { getImageUrl } from "utils";
import { IPreviewArticleState } from "modules/Interface";
import KeywordPill from "./KeywordPill";
import DonutChart from "./DonutChart";
import CompanyCard from "../../Content/Shared/CompanyCard";
import { useRef } from "react";
import Highlighter from "react-highlight-words";
import ButtonRightIcon from "modules/Common/Components/ButtonRightIcon";
import Send from "../../../assets/img/icons/Send.svg";
import { ReactComponent as SearchIcon } from "../../../assets/img/icons/search.svg";
import { ReactComponent as CrossIcon } from "../../../assets/img/icons/cross.svg";
import { closeArticleSidebarPreview } from "modules/GlobalActions";
import ShareMenuPopup from "modules/Common/Components/ShareMenuPopup";

interface IArticleSidebarPreviewInDetailProfileProps {
  articleSidebarPreviewInDetailProfile?: boolean;
}

const ArticlePreviewSidebar: FC<IArticleSidebarPreviewInDetailProfileProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [readMore, setReadMore] = useState(false);
  const [scrollCheck, setScrollCheck] = useState(false);
  const [showSearchTermDiv, setShowSearchTermDiv] = useState(false);
  const [repeatedSearchTermsArray, setRepeatedSearchTermsArray] = useState<string[]>([]);
  const [modifiedSearchTermsState, setModifiedSearchTermsState] = useState([]);

  // Getting data for Article Preview from redux store
  const { openPreview, activeArticle, searchTerms } = useSelector<RootState, IPreviewArticleState>(
    (state) => state.previewArticle
  );
  const includedKeywords = useSelector((state: RootState) => state.searchArticles.includedKeywords);
  const advancedSearchCheck = useSelector((state: RootState) => state.uiReducer.advancedSearchCheck);

  const {
    title,
    entities,
    website,
    tags,
    keyterms,
    concepts,
    publishDate,
    description,
    article_link,
    imgUrl,
    id,
  } = activeArticle;

  const refIn = useRef<HTMLDivElement>(null);

  const overFlowHidden = () => {
    // it will hide the scroll for the main site when article preview is opened
    const body = document.querySelector("body") as HTMLBodyElement;
    body.style.overflow = "hidden";
    body.style.paddingRight = "16px";
  };
  const overFlowVisible = () => {
    // it will hide the scroll for the main site when article preview is opened
    const body = document.querySelector("body") as HTMLBodyElement;
    body.style.overflow = "visible";
    body.style.paddingRight = "0px";
  };

  useEffect(() => {
    if (openPreview) overFlowHidden();
    if (!openPreview) {
      overFlowVisible();
      setModifiedSearchTermsState([]);
    }
    setOpen(openPreview);
    focus();
    setReadMore(false);
    setShowSearchTermDiv(false);
    if (openPreview) {
      const modifiedSearchTerms: any = [];
      let termsToBeHighlighted: any = [];
      if (window.location.pathname === "/campaigns/editionedit/source" && advancedSearchCheck)
        termsToBeHighlighted = includedKeywords;
      else termsToBeHighlighted = searchTerms;

      termsToBeHighlighted?.map((term: string) => {
        const termArray = term.toLowerCase().split(" and ");
        if (termArray.indexOf("and")) {
          termArray.map((subTerm) => {
            if (subTerm !== "and") {
              modifiedSearchTerms.push(subTerm);
            }
          });
        }

        // const splitTermArray = term.toLowerCase()?.split("and");

        // if (splitTermArray.length > 1) {
        //   splitTermArray?.map((splitTerm: string) => {
        //     if (splitTerm.toLowerCase()?.trim() !== "and") {
        //       modifiedSearchTerms?.push(splitTerm.trim());
        //     }
        //   });
        // } else {
        //   modifiedSearchTerms?.push(term.trim());
        // }
        setModifiedSearchTermsState(modifiedSearchTerms);
      });
    }
  }, [openPreview]);

  useEffect(() => {
    let emptyArray: any = [];
    modifiedSearchTermsState?.map((tag: any, index: number) => {
      if (occurrences(title, tag) + occurrences(description, tag) > 0) {
        setShowSearchTermDiv(true);
        if (emptyArray.indexOf(tag) === -1) emptyArray.push(tag);
      }

      if (index + 1 === modifiedSearchTermsState.length) {
        setRepeatedSearchTermsArray(emptyArray);
      }
    });
  }, [title, description]);

  const focus = () => {
    if (refIn.current !== null) {
      refIn.current.scrollTo(0, 0);
    }
  };

  const NoDataFound = () => <div className="no-data">No data found</div>;

  // const tagsArray = tags?.length === 0 ? [] : tags?.split(",");

  const occurrences = (string: any, subString: string, allowOverlapping?: boolean) => {
    // This function returns the number of a substring repeated in string
    string += "";
    subString += "";
    if (subString.length <= 0) return string.length + 1;
    const stringInLowerCase = string.toLowerCase();
    const subStringInLowerCase = subString.toLowerCase();
    var n = 0,
      pos = 0,
      step = allowOverlapping ? 1 : subString.length;

    while (true) {
      pos = stringInLowerCase.indexOf(subStringInLowerCase, pos);
      if (pos >= 0) {
        ++n;
        pos += step;
      } else break;
    }
    return n;
  };

  const crossClickHandler = () => {
    setOpen(false);
    closeArticleSidebarPreview(props.articleSidebarPreviewInDetailProfile);
  };

  const scrollHandler = () => {
    setScrollCheck(!scrollCheck);
  };

  return (
    <Wrapper className={` ${open ? "open" : ""}`} readMore={readMore}>
      <div onScroll={scrollHandler} ref={refIn} className="container">
        <div className="icon-cross">
          <CrossIcon style={{ cursor: "pointer" }} onClick={crossClickHandler} />
        </div>
        <div className="article">
          <h3 className="title ">
            {title && (
              <Highlighter
                highlightClassName="YourHighlightClass"
                searchWords={modifiedSearchTermsState}
                autoEscape={true}
                textToHighlight={title}
              />
            )}
          </h3>

          <div className="text-content">
            {description ? (
              <>
                {imgUrl === "" || imgUrl === undefined ? null : (
                  <img
                    className="article-img"
                    src={getImageUrl(imgUrl, 292, 144, id)}
                    onError={(event) => (event.target.style.display = "none")}
                    alt="article-img"
                  />
                )}
                <br />
                <div className={`description-text ${readMore ? "" : " read-less"}`}>
                  {!readMore ? (
                    <Highlighter
                      highlightClassName="YourHighlightClass"
                      searchWords={modifiedSearchTermsState}
                      autoEscape={true}
                      textToHighlight={description}
                    />
                  ) : (
                    description.split("\n").map((paragraph: string, index: number) => (
                      <div key={index}>
                        {paragraph && (
                          <>
                            <br />
                            <Highlighter
                              highlightClassName="YourHighlightClass"
                              searchWords={modifiedSearchTermsState}
                              autoEscape={true}
                              textToHighlight={paragraph}
                            />
                            <br />
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <PreviewArticleContentLoader />
            )}
          </div>

          <div className="meta-data-container">
            <span className="website-link padding-sm">
              {/* <a href={`https://${website}`} target="_blank" rel="noreferrer"> */}
              {website}
              {/* </a> */}
            </span>
            |<div className="published-time padding-sm">{publishDate}</div>
            {/* |<div className="entities-list-text">{tags}</div> */}
          </div>
        </div>
        <div className="d-flex button-div-style">
          {/* <a href="" target="_blank"> */}
          <ShareMenuPopup
            scrollCheck={scrollCheck}
            id={activeArticle.id}
            inArticlePreview={true}
            // isCurated={props.isCurated}
            // isFav={props.isFav}
            data={activeArticle}
          // onClick={removeOnClickBubble}
          >
            <ButtonRightIcon label="Send to..." icon={Send} />
          </ShareMenuPopup>
          {/* </a> */}
          <span className="d-flex justify-content-end">
            <ButtonRightIcon
              style={{ marginLeft: 10, marginRight: 2 }}
              onClick={() => setReadMore(!readMore)}
              label={readMore ? "Read less" : "Read more"}
              noIcon={true}
            />
            <a style={{ marginLeft: 10 }} href={article_link} target="_blank" rel="noreferrer">
              <ButtonRightIcon label="Visit website" noIcon={true} />
            </a>
          </span>
        </div>
        {modifiedSearchTermsState.length > 0 && showSearchTermDiv && (
          <div className="search-text-wrapper">
            <div className="search-text-style">
              <SearchIcon style={{ width: 19, height: 19, marginBottom: 5 }} /> Search terms found
            </div>

            {repeatedSearchTermsArray?.map((tag: any, index: number) => {
              // const trimmedTag = tag.trim();
              // const capitalizedTrimmedTagArray = trimmedTag.split(" ").map((term: string) => {
              //   return capitalizeFirstLetter(term);
              // });
              // const capitalizedTrimmedTag = capitalizedTrimmedTagArray.join(" ");
              return (
                <span key={index} className="green-text-style">
                  {`${index > 0 ? "," : ""} ${tag}`}
                  <span className="black-text-style">{` (${occurrences(title, tag) + occurrences(description, tag)
                    })`}</span>
                </span>
              );
            })}
          </div>
        )}
        {/* Keywords section */}
        <div className="heading-section">
          <span className="search-bulb-icon">
            <Lightbulb />
          </span>
          Keywords mentioned
        </div>
        <div className="content-section">
          {keyterms ? (
            keyterms.length === 0 ? (
              <NoDataFound />
            ) : (
              keyterms?.map((item: any, index: number) => <KeywordPill key={index} keyword={item.name} />)
            )
          ) : (
            <PreviewArticleContentLoader />
          )}
        </div>

        {/* Concepts section */}
        <div className="heading-section">
          <span className="search-bulb-icon">
            <Lightbulb />
          </span>
          Concepts mentioned
        </div>
        <div className="content-section">
          {/* add data in props */}
          {concepts ? (
            concepts.length === 0 ? (
              <NoDataFound />
            ) : (
              concepts?.length > 0 && <DonutChart data={concepts} />
            )
          ) : (
            <PreviewArticleContentLoader />
          )}
        </div>

        {/* Entities Section */}
        <div className="heading-section">
          <span className="search-bulb-icon">
            <Lightbulb />
          </span>
          Entities mentioned
        </div>
        <div className="content-section">
          {entities ? (
            entities?.length === 0 ? (
              <NoDataFound />
            ) : (
              entities?.map((item: any, index: number) => (
                <CompanyCard key={index} entity="Entity" companyName={item?.name} mentions={item?.mentions} />
              ))
            )
          ) : (
            <PreviewArticleContentLoader />
          )}
        </div>
      </div>
      {/* toggle open class to view right sidebar */}
    </Wrapper>
  );
};

export default ArticlePreviewSidebar;

const Wrapper = styled.div<{ readMore?: boolean }>`
  right: -530px;
  position: fixed;
  width: 530px;
  top: 0;
  z-index: 14;
  transition: 0.5s;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: 410px;
    right: -410px;
  }
  &.open {
    right: 0px;
    transition: 0.5s;
  }
  .article-img {
    width: 100%;
    height: 12em;
    margin-bottom: 5px;
    &[src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="] {
      display: none !important;
    }
  }
  .YourHighlightClass {
    padding: 0;
    background-color: #fbce17;
  }
  .button-div-style {
    width: 100%;
    margin-top: 15px;
    padding-bottom: 24px;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      bottom: 0px;
      left: 30px;
      width: 80%;
      height: 1.5px;
      background-color: rgb(231, 231, 231);
    }
    a button .label {
      padding: 5px 0;
    }
  }

  .search-text-wrapper {
    position: relative;
    padding-bottom: 18px;
    width: 100%;
    &::before {
      content: "";
      position: absolute;
      bottom: 0px;
      left: 30px;
      width: 80%;
      height: 1.5px;
      background-color: rgb(231, 231, 231);
    }
  }
  .search-text-style {
    font-family: "Roboto Slab";
    font-weight: 400;
    color: #424b50;
    font-size: 22px;
    line-height: 1.192;
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .green-text-style {
    font-family: "Roboto";
    font-weight: 500;
    color: #27ce0a;
    font-size: 14px;
    line-height: 1.873;
    text-transform: capitalize;
    .black-text-style {
      color: #424b50;
    }
  }
  .icon-cross {
    width: inherit;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 2em;
    margin-top: -3em;
  }
  .no-data {
    font-family: "robotoregular";
    padding: 0 45px;
    width: 100%;
    color: #7e7e7e;
  }
  .container {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    height: 100vh;
    background: #fff;
    border-left: 1px solid #cfcfcf;
    float: right;
    width: 100%;
    padding: 130px 60px;
    overflow: auto;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding: 100px 50px;
    }
    .title {
      font-family: "Roboto Slab", serif;
      font-size: 30px;
      font-weight: 800;
      line-height: 40px;
      word-break: break-word;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 24px;
        line-height: 32px;
      }
    }
    .text-content {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      display: -webkit-box;
      ${(props) => (props.readMore ? "" : "-webkit-line-clamp: 4;")}
      /* -webkit-line-clamp: ${(props) => (props.readMore ? 12 : 4)}; */
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-word;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        line-height: 16px;
      }
      svg {
        max-width: 100%;
      }
      div,
      span {
        display: block;
        float: left;
      }
      .description-text {
        color: #7e7e7e;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
        font-family: "Roboto";
        position: relative;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 10px;
          line-height: 14px;
        }
        &.read-less {
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
          display: -webkit-box;
          br {
            display: none;
          }
          > div,
          > span {
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-word;
            display: -webkit-box;
            * {
              display: inline;
              text-align: left;
              clear: both;
              float: none;
            }
          }
        }
        div,
        span {
          display: block;
          float: left;
          color: #7e7e7e;
          font-size: 13px;
          font-weight: 500;
          line-height: 18px;
          font-family: "Roboto";
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 10px;
            line-height: 14px;
          }
          marker,
          span {
            display: inline;
            text-align: left;
            clear: both;
            float: none;
          }
        }
      }
    }

    .line-clamp {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .heading-section {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      font-size: 25px;
      line-height: 42px;
      font-weight: 600;
      padding: 6px 5px;
      margin: 20px 0 5px 0;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 16px;
        line-height: 34px;
        margin: 10px 0 5px 0;
      }
    }
    .content-section {
      display: flex;
      width: 100%;
      height: fit-content;
      flex-wrap: wrap;
    }

    .entities-item {
      height: 100px;
      width: 130px;
      margin-left: 7px;
      background-color: #001760;
      border-radius: 8px;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      margin: 5px;
    }

    .search-bulb-icon {
      margin-right: 7px;
      padding-right: 7px;
      float: left;
      cursor: pointer;
      svg {
        float: left;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 18px;
        }
      }
    }
    .website-link {
      color: #db3725;
      font-family: "robotomedium", sans-serif;
      font-weight: 500;
      padding-left: 0;
    }

    .meta-data-container {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: grey;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
        line-height: 18px;
      }
    }

    .vertical-divider {
      height: 16px;
      border: 0;
      border-right: 3px solid #7e7e7e;
      margin: 0 10px;
    }

    .published-time {
      color: #7e7e7e;
      display: inline-block;
      // font-size: 10px;
      font-family: "robotomedium", sans-serif;
      font-weight: 500;
      white-space: nowrap;
    }

    .tags {
      color: #7e7e7e;
      font-weight: 500;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    .entities-list-text {
      font-family: "robotomedium", sans-serif;
      padding: 3px 7px;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-transform: capitalize;
    }
  }
`;
